import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuestionDisplay from './components/QuestionDisplay.js';
import AnswerSubmittedScreen from './components/AnswerSubmittedScreen.js';
import LoadingScreen from '../LoadingScreen/LoadingScreen.js';
import ErrorScreen from '../LoadingScreen/ErrorScreen.js';
import { useSession, useSessionActions } from '../../context/SessionContext.js';
import './QuizScreen.css';

const QuizScreen = () => {
  const navigate = useNavigate();

  // Session state from provider
  const session = useSession();
  const { submitAnswer, skipQuestion } =
    useSessionActions();

  // Destructure session state for easier access
  const {
    // sessionId,
    currentQuestion,
    currentQuestionNumber,
    totalQuestions,
    equivalentCorrectAnswers,
    isLoading,
    error,
    sessionStarted,
    sessionEnded,
    // serverDate
  } = session;

  // Local UI state
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Calculate derived values
  // const progress = totalQuestions > 0 ? (currentQuestionNumber / totalQuestions) * 100 : 0;
  const hasNextQuestion = currentQuestionNumber < totalQuestions;

  // Initialize session on component mount - only once!
  useEffect(() => {
    console.log(
      '🔄 QuizScreen: useEffect triggered, isInitialized:',
      isInitialized,
      'userId:',
      session.userId
    );

    // Prevent multiple initializations
    if (isInitialized || !session.userId) {
      console.log(
        '⏭️ QuizScreen: Skipping initialization - already initialized or no userId'
      );
      return;
    }

    const initializeSession = async () => {
      try {
        console.log('🔍 QuizScreen: Checking if session is already started...');
        setIsInitialized(true); // Set immediately to prevent re-runs

        // Check if session is already started (should be if coming from EventDetailsScreen)
        if (!sessionStarted) {
          console.log('❌ QuizScreen: No active session found, redirecting to start');
          navigate('/start', { state: { error: 'Nessuna sessione attiva trovata' } });
          return;
        }

        console.log('✅ QuizScreen: Session is active, ready to show quiz');
      } catch (error) {
        console.error('💥 QuizScreen: Failed to initialize:', error);
        navigate('/start', {
          state: { error: 'Errore durante l\'inizializzazione del quiz' },
        });
      }
    };

    initializeSession();
  }, [
    session.userId,
    isInitialized,
    sessionStarted,
    navigate,
  ]); // Include all dependencies

  // Navigate to end screen when session ends
  useEffect(() => {
    if (sessionEnded) {
      navigate('/end', {
        state: {
          totalQuestions,
          correctAnswers: equivalentCorrectAnswers,
          sessionCompleted: true,
        },
      });
    }
  }, [sessionEnded, navigate, totalQuestions, equivalentCorrectAnswers]);

  // Reset local state when question changes
  useEffect(() => {
    setSelectedAnswer(null);
    setTextAnswer('');
    setShowFeedback(false);
  }, [currentQuestionNumber]);

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleTextChange = (text) => {
    setTextAnswer(text);
  };

  const handleSubmitAnswer = async (forceSkip = false) => {
    if (!currentQuestion) return;

    const isMultipleChoice = currentQuestion.type === 'standard';
    const isFillText = currentQuestion.type === 'fill_text';

    // Validation - only block submission if it's not a forced skip
    if (!forceSkip) {
      if (isMultipleChoice && selectedAnswer === null) return;
      if (isFillText && textAnswer.trim() === '') return;
    }

    setIsSubmitting(true);

    try {
      if (forceSkip) {
        await skipQuestion();
      } else {
        // Submit the answer based on question type
        if (isMultipleChoice) {
          await submitAnswer(selectedAnswer, null);
        } else if (isFillText) {
          await submitAnswer(null, textAnswer.trim());
        }
      }

      // Show feedback screen
      setShowFeedback(true);
    } catch (error) {
      console.error('Failed to submit answer:', error);
      // Handle error - maybe show error message to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextQuestion = () => {
    setShowFeedback(false);
    // The useQuizSession hook handles the actual progression
    // If it's the last question, the useEffect will navigate to /end
  };

  // Loading states
  if (isLoading && !sessionStarted) {
    return <LoadingScreen autoNavigate={false} />;
  }


  // Error states
  if (error) {
    return (
      <ErrorScreen
        title='Errore Quiz'
        message={error}
        onRetry={() => window.location.reload()}
        showFooter={false}
      />
    );
  }

  // No question loaded yet
  if (!currentQuestion) {
    return (
      <LoadingScreen
      />
    );
  }

  // Show feedback screen between questions
  if (showFeedback) {
    return (
      <AnswerSubmittedScreen
        onNextQuestion={handleNextQuestion}
        isLastQuestion={!hasNextQuestion}
        currentQuestion={currentQuestionNumber}
        totalQuestions={totalQuestions}
      />
    );
  }

  // Main quiz interface
  return (
    <div className='quiz-screen'>
      {/* Debug info - remove in production */}
      {console.log('🎮 QuizScreen: Rendering main interface', {
        isLoading,
        isSubmitting,
        sessionStarted,
        currentQuestion: !!currentQuestion,
        currentQuestionNumber,
        totalQuestions,
        questionType: currentQuestion?.type,
      })}

      {/* Progress indicator */}
      {/* <div className="quiz-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <span className="progress-text">
          Domanda {currentQuestionNumber} di {totalQuestions}
        </span>
      </div> */}

      <QuestionDisplay
        question={currentQuestion}
        questionNumber={currentQuestionNumber}
        selectedAnswer={selectedAnswer}
        textAnswer={textAnswer}
        onAnswerSelect={handleAnswerSelect}
        onTextChange={handleTextChange}
        onSubmitAnswer={handleSubmitAnswer}
        isSubmitting={isSubmitting || isLoading}
        showSkipOption={true}
      />
    </div>
  );
};

export default QuizScreen;
